import { useState, useCallback } from 'react';
import { authService } from '../services/authService';
import { IUser } from '../types';

interface UseAuthState {
  user: IUser | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

export const useAuth = () => {
  const [state, setState] = useState<UseAuthState>({
    user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '') : null,
    token: localStorage.getItem('authToken'),
    loading: false,
    error: null,
    isAuthenticated: !!localStorage.getItem('authToken'),
  });

  const register = useCallback(
    async (data: { firstName: string; lastName: string; email: string; password: string; phone?: string }) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const response = await authService.register(data);
        setState((prev) => ({
          ...prev,
          user: response.user,
          token: response.token,
          isAuthenticated: true,
          loading: false,
        }));
        return response;
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Registration failed';
        setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
        throw error;
      }
    },
    []
  );

  const login = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await authService.login(email, password);
      setState((prev) => ({
        ...prev,
        user: response.user,
        token: response.token,
        isAuthenticated: true,
        loading: false,
      }));
      return response;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setState({
      user: null,
      token: null,
      loading: false,
      error: null,
      isAuthenticated: false,
    });
  }, []);

  return { ...state, register, login, logout };
};
